import { createFileRoute, Link, useParams, useNavigate } from '@tanstack/react-router';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useStore, useIsAppLoaded } from '../../../../store';
import { useAppContext } from '../../../../contexts/AppContext';
import { UploadCard, Badge, CopyButton } from '../../../../components/shared';
import { AddVersionDialog } from '../../../../components/AddVersionDialog';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ShareVersionDialog } from '../../../../components/ShareVersionDialog';
import { VersionListSkeleton } from '../../../../components/skeletons';
import { LocalVersionsSection } from '../../../../components/localVersions';
import { EditVersionDialog } from '../../../../components/EditVersionDialog';
import { send, waitForReady } from '../../../../ws';
import { waitForAck } from '../../../../utils/waitForAck';
import { correlationId } from '@interlucent/ids';
import { storePreviewContext } from '../../../preview.$';
import { createPreviewShare } from '../../../../utils/createPreviewShare';
import type { AppVersion } from '@interlucent/organization-protocol';
import { logger } from '../../../../log';
import styles from '../../../../components/ProjectsView.module.css';

const log = logger('app-detail');

export const Route = createFileRoute('/projects/$projectId/applications/$appId')({
  component: ApplicationDetailRoute,
});

function ApplicationDetailRoute() {
  const { projectId, appId } = useParams({ from: '/projects/$projectId/applications/$appId' });
  const navigate = useNavigate();
  const { handleUploadStarted, handleCancelUpload, handlePauseUpload, handleResumeUpload } = useAppContext();
  const isConnected = useStore(s => s.connectionState === 'connected');
  const projects = useStore(s => s.projects);
  const applications = useStore(s => s.applications);
  const versions = useStore(s => s.versions);
  const organization = useStore(s => s.organization);
  const uploadProgress = useStore(s => s.uploadProgress);

  const project = projects[projectId];

  const [addVersionDialog, setAddVersionDialog] = useState(false);
  const [replaceVersion, setReplaceVersion] = useState<{ versionId: string } | null>(null);
  const [shareVersion, setShareVersion] = useState<{ versionId: string; versionName: string } | null>(null);
  const [editVersion, setEditVersion] = useState<AppVersion | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [renameError, setRenameError] = useState('');

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    requireConfirmation?: boolean;
    confirmationPhrase?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const application = applications[appId];
  // Filter to only uploaded versions (not local versions)
  const appVersions = Object.values(versions).filter(v => v.appId === appId && !v.isLocal);
  // Resolver-correct pool coverage from the WS snapshot's `coveringPools`
  // slice (populated from pool_materialized_entries in the gateway) +
  // AppPoolCoverageChanged events.
  const coverage = useStore(s => s.appPoolCoverage[appId] ?? null);

  const versionQuota = organization?.planTier?.quotaMaxVersionsPerApp ?? 5;
  const localVersionQuota = organization?.planTier?.quotaMaxLocalVersionsPerApp ?? 2;

  const appUploads = useMemo(() =>
    Object.entries(uploadProgress).filter(
      ([, progress]) => progress.appId === appId
    ),
    [uploadProgress, appId]
  );

  const inProgressNewVersions = appUploads.filter(
    ([, progress]) => !progress.replaceVersionId
  ).length;
  const totalVersionCount = appVersions.length + inProgressNewVersions;

  const appLoaded = useIsAppLoaded(appId);

  const handleDeleteVersion = (versionId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Version',
      message: `This will permanently delete version "${versionId}".`,
      requireConfirmation: true,
      confirmationPhrase: versionId,
      onConfirm: () => {
        if (isConnected) {
          send('version.delete', { versionId });
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleSetDefaultVersion = (versionId: string) => {
    if (isConnected) {
      send('application.setDefaultVersion', { appId, versionId });
    }
  };

  const handleRename = async () => {
    const trimmed = editName.trim();
    if (!trimmed) {
      setRenameError('Application name cannot be empty.');
      return;
    }
    if (trimmed === application?.name) {
      setIsEditingName(false);
      return;
    }

    setIsSavingName(true);
    setRenameError('');

    try {
      await waitForReady();
      const corr = correlationId();
      send('application.update', {
        id: appId,
        name: trimmed,
        rowVersion: application?.rowVersion ?? 0,
      }, corr);

      await waitForAck<{ ok: boolean; rowVersion: number }>(corr);
      setIsEditingName(false);
    } catch (error: any) {
      setRenameError(error?.message || 'Failed to rename application');
    } finally {
      setIsSavingName(false);
    }
  };

  const handleCancelRename = () => {
    setEditName(application?.name ?? '');
    setRenameError('');
    setIsEditingName(false);
  };

  // Sync editName when store updates while not editing
  useEffect(() => {
    if (application && !isEditingName) {
      setEditName(application.name);
    }
  }, [application?.name, isEditingName]);

  // Hover-prefetch state: create preview share on hover so it's ready by click time
  const [prefetchState, setPrefetchState] = useState<Record<string, {
    status: 'idle' | 'prefetching' | 'ready' | 'failed';
    shareId?: string;
  }>>({});
  // Ref-based deduplication: tracks in-flight + completed requests per version (immune to React batching)
  const inflightShares = useRef<Map<string, Promise<string>>>(new Map());
  const abortControllerRef = useRef<AbortController>(new AbortController());

  // Create fresh AbortController on each mount (handles strict mode double-mount),
  // abort in-flight prefetch requests on unmount
  useEffect(() => {
    abortControllerRef.current = new AbortController();
    return () => {
      abortControllerRef.current.abort();
    };
  }, []);

  // Start prefetching a preview share for a version (called on hover or click)
  const startPrefetch = useCallback(async (version: AppVersion): Promise<string> => {
    // Ref-based guard: synchronous check avoids React state batching issues
    const existing = inflightShares.current.get(version.id);
    if (existing) return existing;

    setPrefetchState(prev => ({
      ...prev,
      [version.id]: { status: 'prefetching' },
    }));

    await waitForReady();

    const promise = createPreviewShare({
      projectId,
      appId,
      versionId: version.id,
      expiresInMinutes: 120,
      signal: abortControllerRef.current.signal,
    }).then(({ id }) => {
      setPrefetchState(prev => ({
        ...prev,
        [version.id]: { status: 'ready', shareId: id },
      }));
      return id;
    }).catch((err) => {
      // Remove from inflight on failure so retry is possible
      inflightShares.current.delete(version.id);
      // AbortErrors are expected on unmount — skip log and state update
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      log.error('failed to prefetch preview share', err);
      setPrefetchState(prev => ({
        ...prev,
        [version.id]: { status: 'failed' },
      }));
      throw err;
    });

    inflightShares.current.set(version.id, promise);
    return promise;
  }, [projectId, appId]);

  // Hover handler — kick off prefetch in background
  const handlePreviewHover = useCallback((version: AppVersion) => {
    startPrefetch(version)?.catch(() => {}); // errors already handled in startPrefetch
  }, [startPrefetch]);

  // Click handler — navigate immediately if ready, otherwise wait for prefetch
  const handlePreviewClick = useCallback(async (version: AppVersion) => {
    const current = prefetchState[version.id];
    let shareId: string | undefined;

    if (current?.status === 'ready' && current.shareId) {
      shareId = current.shareId;
    } else {
      // Touch device path or prefetch still in flight
      try {
        const existingPromise = inflightShares.current.get(version.id);
        if (existingPromise) {
          shareId = await existingPromise;
        } else {
          // No prefetch started yet (touch device) — start and wait
          shareId = await startPrefetch(version);
        }
      } catch (err) {
        log.error('failed to create preview share', err);
        return;
      }
    }

    if (!shareId) return;

    storePreviewContext({
      title: `${application?.name || appId} - ${version.id}`,
      projectId,
      appId,
      versionId: version.id,
    });
    navigate({
      to: '/preview/$',
      params: { _splat: shareId },
    });
  }, [navigate, projectId, appId, application, prefetchState, startPrefetch]);

  // Show skeleton while app data is loading
  if (!appLoaded) {
    return (
      <div className={styles.container}>
        <div className={styles.breadcrumb}>
          <Link to="/projects" className={styles.breadcrumbLink}>
            Projects
          </Link>
          <span className={styles.breadcrumbSeparator}>/</span>
          <Link to="/projects/$projectId/applications" params={{ projectId }} className={styles.breadcrumbLink}>
            {project?.name || 'Project'}
          </Link>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span>Loading...</span>
        </div>
        <VersionListSkeleton count={3} />
      </div>
    );
  }

  // Show error only after data is loaded and app truly doesn't exist
  if (!application) {
    return (
      <div className={styles.container}>
        <div className={styles.errorMessage}>
          <span className={styles.errorText}>Application not found</span>
          <Link to="/projects/$projectId/applications" params={{ projectId }} className={styles.errorClose}>
            ← Back to Applications
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumb}>
        <Link to="/projects" className={styles.breadcrumbLink}>
          Projects
        </Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <Link to="/projects/$projectId/applications" params={{ projectId }} className={styles.breadcrumbLink}>
          {project?.name || 'Project'}
        </Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span>{application.name}</span>
      </div>

      <div className={styles.detailHeader}>
        <div className={styles.detailHeaderContent}>
          {isEditingName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <input
                type="text"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleRename();
                  if (e.key === 'Escape') handleCancelRename();
                }}
                autoFocus
                style={{ fontSize: 'var(--text-xl)', fontWeight: 600, padding: 'var(--space-1) var(--space-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', outline: 'none' }}
              />
              <button
                onClick={handleRename}
                disabled={isSavingName || !editName.trim()}
                className={`${styles.buttonSmall} ${styles.buttonAction}`}
              >
                {isSavingName ? 'Saving\u2026' : 'Save'}
              </button>
              <button
                onClick={handleCancelRename}
                disabled={isSavingName}
                className={`${styles.buttonSmall} ${styles.buttonSecondary}`}
              >
                Cancel
              </button>
            </div>
          ) : (
            <h1
              className={styles.detailTitle}
              onClick={() => { setEditName(application.name); setRenameError(''); setIsEditingName(true); }}
              style={{ cursor: 'pointer' }}
              title="Click to rename"
            >
              {application.name}
              <span style={{ marginLeft: 'var(--space-1)', opacity: 0.4, fontSize: '0.6em' }}>&#9998;</span>
            </h1>
          )}
        </div>
        <button
          onClick={() => setAddVersionDialog(true)}
          disabled={!isConnected || totalVersionCount >= versionQuota}
          className={styles.button}
        >
          Add Version
        </button>
      </div>

      {renameError && (
        <div className={styles.errorMessage} style={{ marginBottom: 'var(--space-2)' }}>
          <span className={styles.errorText}>{renameError}</span>
          <button
            type="button"
            onClick={() => setRenameError('')}
            className={styles.errorClose}
            aria-label="Dismiss error"
          >
            &times;
          </button>
        </div>
      )}

      <div className={styles.metadataStrip}>
        <div className={styles.metadataItem}>
          <div className={styles.metadataLabel}>Application ID</div>
          <div className={styles.metadataValue}>
            {application.id}
            <CopyButton value={application.id} aria-label="Copy Application ID" />
          </div>
        </div>
        <div className={styles.metadataItem}>
          <div className={styles.metadataLabel}>Versions</div>
          <div className={styles.metadataValue}>
            {totalVersionCount} / {versionQuota}
            {appUploads.length > 0 && (
              <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', marginLeft: 'var(--space-1)' }}>
                (+{appUploads.length} uploading)
              </span>
            )}
          </div>
        </div>
        <div className={styles.metadataItem}>
          <div className={styles.metadataLabel}>Default Version</div>
          <div className={styles.metadataValue}>
            {application.defaultVersionId
              ? versions[application.defaultVersionId]?.id || 'N/A'
              : 'None set'
            }
          </div>
        </div>
        <div className={styles.metadataItem}>
          <div className={styles.metadataLabel}>Profiles</div>
          <div className={styles.metadataValue}>{new Set((coverage ?? []).map(c => c.poolId)).size}</div>
        </div>
      </div>

      {(() => {
        // Coverage entries are one row per (pool, version); collapse to
        // one pill per pool for display, with the active state inherited
        // from the pool.
        const byPool = new Map<string, { id: string; name: string; active: boolean }>();
        for (const c of coverage ?? []) {
          if (!byPool.has(c.poolId)) {
            byPool.set(c.poolId, { id: c.poolId, name: c.poolName, active: c.active });
          }
        }
        const pills = [...byPool.values()].sort((a, b) => a.name.localeCompare(b.name));
        if (pills.length === 0) return null;
        return (
          <div className={styles.inlineBadgeRow}>
            <span className={styles.inlineBadgeLabel}>Mapped to:</span>
            {pills.map(p => (
              <Badge key={p.id} variant={p.active ? 'success' : 'default'}>
                {p.name}
              </Badge>
            ))}
          </div>
        );
      })()}

      <div className={styles.detailSection}>
        <div className={styles.detailSectionHeader}>
          <h2 className={styles.detailSectionTitle}>Versions</h2>
        </div>

        {!appLoaded ? (
          <VersionListSkeleton count={3} />
        ) : appVersions.length === 0 && appUploads.length === 0 ? (
          <div className={styles.emptyState}>
            No versions yet. Add a version to get started.
          </div>
        ) : (
          <div className={styles.versionContainer}>
            {appUploads.map(([uploadId, progress]) => (
              <UploadCard
                key={uploadId}
                uploadId={uploadId}
                progress={progress}
                onPause={handlePauseUpload}
                onResume={handleResumeUpload}
                onCancel={handleCancelUpload}
              />
            ))}

            {appVersions.map(version => {
              const isDefault = version.id === application.defaultVersionId;
              const isBeingReplaced = appUploads.some(([, progress]) => progress.replaceVersionId === version.id);

              return (
                <div
                  key={version.id}
                  className={`${styles.versionCard} ${isDefault ? styles.default : ''} ${isBeingReplaced ? styles.beingReplaced : ''}`}
                >
                  <button
                    type="button"
                    className={[
                      styles.thumbnailLink,
                      prefetchState[version.id]?.status === 'prefetching' ? styles.prefetching : '',
                      prefetchState[version.id]?.status === 'failed' ? styles.failed : '',
                    ].join(' ')}
                    title="Preview version"
                    disabled={!isConnected}
                    onMouseEnter={() => handlePreviewHover(version)}
                    onClick={() => handlePreviewClick(version)}
                  >
                    <div className={styles.thumbnail}>
                      <svg className={styles.thumbnailIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                  </button>
                  <div className={styles.versionInfo}>
                    <div className={styles.versionNameRow}>
                      <span className={styles.versionName}>{version.id}</span>
                      {isDefault && (
                        <span className={styles.versionBadge}>Default</span>
                      )}
                    </div>
                    <div className={styles.versionUrl}>
                      {appId}@{version.id}
                      <CopyButton value={`${appId}@${version.id}`} aria-label="Copy Version Reference" />
                    </div>
                  </div>
                  <div className={styles.versionActions}>
                    <button
                      onClick={() => setEditVersion(version)}
                      className={`${styles.buttonSmall} ${styles.buttonAction}`}
                      title="View version details"
                    >
                      Details
                    </button>
                    <button
                      onClick={() => setShareVersion({ versionId: version.id, versionName: version.id })}
                      disabled={!isConnected}
                      className={`${styles.buttonSmall} ${styles.buttonAction}`}
                      title="Create share link"
                    >
                      Share
                    </button>
                    {!isDefault && (
                      <button
                        onClick={() => handleSetDefaultVersion(version.id)}
                        disabled={!isConnected}
                        className={`${styles.buttonSmall} ${styles.buttonAction}`}
                      >
                        Set as Default
                      </button>
                    )}
                    <button
                      onClick={() => setReplaceVersion({ versionId: version.id })}
                      disabled={!isConnected}
                      className={`${styles.buttonSmall} ${styles.buttonSecondary}`}
                    >
                      Replace
                    </button>
                    <button
                      onClick={() => handleDeleteVersion(version.id)}
                      className={`${styles.buttonSmall} ${styles.buttonDelete}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Local Versions Section */}
      <LocalVersionsSection
        appId={appId}
        projectId={projectId}
        quota={localVersionQuota}
      />

      {addVersionDialog && (
        <AddVersionDialog
          application={application}
          onClose={() => setAddVersionDialog(false)}
          onUploadStarted={handleUploadStarted}
        />
      )}

      {replaceVersion && (
        <AddVersionDialog
          application={application}
          onClose={() => setReplaceVersion(null)}
          onUploadStarted={handleUploadStarted}
          replaceVersionId={replaceVersion.versionId}
        />
      )}

      {shareVersion && (
        <ShareVersionDialog
          isOpen={true}
          versionId={shareVersion.versionId}
          versionName={shareVersion.versionName}
          appId={appId}
          projectId={projectId}
          onClose={() => setShareVersion(null)}
        />
      )}

      {editVersion && (
        <EditVersionDialog
          isOpen={true}
          version={editVersion}
          onClose={() => setEditVersion(null)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        confirmText="Delete"
        requireConfirmation={confirmDialog.requireConfirmation}
        confirmationPhrase={confirmDialog.confirmationPhrase}
      />
    </div>
  );
}
